import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import {
  DEFAULT_OPTION,
  PAYMENT_METHOD_OPTIONS,
  STAGES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import { RecentTransactionsSearchParams, SearchFilters } from "../types/common";
import { GL_MAPPING } from "../hooks/useSummaryData";

export const DEFAULT_FILTERS: SearchFilters = {
  stage: STAGES[0],
  gl: DEFAULT_OPTION,
  marketplaces: [],
  usecases: [],
  paymentMethod: PAYMENT_METHOD_OPTIONS[0],
  timeRange: TIME_RANGES[1],
  date: "",
  consumptionTypes: [],
  deep: false,
};

export function getEndTime(
  range: OptionDefinition,
  date: string
): number | undefined {
  if (range.value === "custom" && date) {
    const d = new Date(date);
    d.setUTCHours(12, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  }
  const now = Math.floor(Date.now() / 1000);
  switch (range.value) {
    case "5min":
      return now - 5 * 60;
    case "15min":
      return now - 15 * 60;
    case "1hour":
      return now - 60 * 60;
    case "24hours":
      return now - 24 * 60 * 60;
    case "1week":
      return now - 7 * 24 * 60 * 60;
    case "1month":
      return now - 30 * 24 * 60 * 60;
    default:
      return undefined;
  }
}

export function parseFiltersFromUrl(params: URLSearchParams): SearchFilters {
  const getValue = (key: string) => params.get(key);

  const toOptionList = (key: string): OptionDefinition[] =>
    params
      .getAll(key)
      .flatMap((v) => v.split(",")) // backwards compatible for when urls used commas
      .filter(Boolean)
      .map((v) => ({ value: v, label: v }));

  const stage = STAGES.find((o) => o.value === getValue("stage")) || STAGES[0];

  const glValue = getValue("gl");
  const gl = glValue
    ? { value: glValue, label: GL_MAPPING[glValue] ?? glValue }
    : DEFAULT_OPTION;

  const paymentMethod =
    PAYMENT_METHOD_OPTIONS.find((o) => o.value === getValue("paymentMethod")) ||
    DEFAULT_OPTION;

  const timeRange =
    TIME_RANGES.find((o) => o.value === getValue("timeRange")) ||
    TIME_RANGES[1];

  const date = getValue("date") ?? "";

  const marketplaces = toOptionList("countries");
  const usecases = toOptionList("usecases");
  const consumptionTypes = toOptionList("consumptionTypes");

  const deep = getValue("deep") === "true";

  return {
    stage,
    gl,
    marketplaces,
    usecases,
    paymentMethod,
    timeRange,
    date,
    consumptionTypes,
    deep,
  };
}

function nonDefault(opt: OptionDefinition): string | undefined {
  return opt.value !== DEFAULT_OPTION.value ? opt.value : undefined;
}

function listValues(list: OptionDefinition[]): string[] | undefined {
  const values = list.map(nonDefault).filter((v): v is string => !!v);

  return values.length ? values : undefined;
}

export function buildApiParams(
  filters: SearchFilters
): RecentTransactionsSearchParams {
  const params: RecentTransactionsSearchParams = {
    stage: filters.stage.value!,
  };

  const gl = nonDefault(filters.gl);
  if (gl) params.gl = gl;

  const countries = listValues(filters.marketplaces);
  if (countries) params.countries = countries;

  const usecases = listValues(filters.usecases);
  if (usecases) params.usecases = usecases;

  const paymentMethod = nonDefault(filters.paymentMethod);
  if (paymentMethod) params.paymentMethod = paymentMethod;

  const consumptionTypes = listValues(filters.consumptionTypes);
  if (consumptionTypes) params.consumptionTypes = consumptionTypes;

  params.endTime = getEndTime(filters.timeRange, filters.date);

  if (filters.deep) {
    params.deep = true;
  }
  return params;
}

export function serializeSearchParams(
  params: RecentTransactionsSearchParams
): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (Array.isArray(val)) {
      val.forEach((v) => sp.append(key, v));
    } else {
      sp.set(key, String(val));
    }
  }
  return sp;
}
